import { Type } from '../../../modules/templates/templateEditor/editorActions'
import {
  Block,
  BlockGroup,
  BlockKind,
  BlockTypes,
  Condition,
  Section,
  TemplateData,
} from '../types/blueprint'

import { getBlockConfig } from './BlockConfigs'
import { TypeOption } from './BlockConfigs/_BaseBlockConfig'

import { TemplateType } from 'types/api.graphql'

const cloneTemplate = (template: TemplateData): TemplateData => ({
  ...template,
  sections: template?.sections?.map(cloneSection),
})

const cloneSection = (section: Section): Section => ({
  ...section,
  blockGroups: section?.blockGroups?.map(cloneBlockGroup),
})

const updateConditionBlockId = (
  condition: Condition,
  idMapping: Record<string, string>
): Condition => {
  if (condition.blockId && idMapping[condition.blockId]) {
    return {
      ...condition,
      blockId: idMapping[condition.blockId],
    }
  }
  return condition
}

const cloneBlockGroup = (blockGroup: BlockGroup): BlockGroup => {
  const clonedBlocks = blockGroup?.blocks?.map(cloneBlock)

  const idMapping = {}
  blockGroup?.blocks?.forEach((block, index) => {
    if (block.id && clonedBlocks[index].id) {
      idMapping[block.id] = clonedBlocks[index].id
    }
  })

  const updatedConditions = blockGroup?.conditions?.map((conditionGroup) =>
    conditionGroup.map((condition) =>
      updateConditionBlockId(condition, idMapping)
    )
  )

  return {
    ...blockGroup,
    blocks: clonedBlocks,
    conditions: updatedConditions,
  }
}

const cloneBlock = (block: Block): Block => ({ ...block })

type Item<T> = T extends 'SECTION'
  ? Section
  : T extends 'BLOCK_GROUP'
    ? BlockGroup
    : T extends 'BLOCK'
      ? Block
      : T extends 'TEMPLATE'
        ? TemplateData
        : never

export const dynamicClone = <T extends Type>(
  type: T,
  item: TemplateData | Section | BlockGroup | Block
): Item<T> => {
  switch (type) {
    case 'TEMPLATE':
      return cloneTemplate(item as TemplateData) as Item<T>
    case 'SECTION':
      return cloneSection(item as Section) as Item<T>
    case 'BLOCK_GROUP':
      return cloneBlockGroup(item as BlockGroup) as Item<T>
    case 'BLOCK':
      return cloneBlock(item as Block) as Item<T>
  }
}

export const findItem = <T extends Type>(
  type: T,
  id: string,
  template: TemplateData
): Item<T> => {
  if (type === 'SECTION') {
    for (const section of template.sections) {
      if (section.id === id) return section as Item<T>
    }
  }

  if (type === 'BLOCK_GROUP') {
    for (const section of template.sections) {
      const blockGroup = section.blockGroups.find((bg) => bg.id === id)
      if (blockGroup) return blockGroup as Item<T>
    }
  }

  if (type == 'BLOCK') {
    for (const section of template.sections) {
      for (const blockGroup of section.blockGroups) {
        const block = blockGroup.blocks.find((b) => b.id === id)
        if (block) return block as Item<T>
      }
    }
  }

  return null
}

export const insertItemAt = (
  template: TemplateData,
  type: Type,
  item: object,
  parentId: string,
  index: number
): TemplateData => {
  const templateClone = cloneTemplate(template)

  if (type === 'SECTION') {
    templateClone.sections.splice(index, 0, item as Section)
  }

  if (type === 'BLOCK_GROUP') {
    for (const section of templateClone.sections) {
      if (section.id === parentId)
        section.blockGroups.splice(index, 0, item as BlockGroup)
    }
  }

  if (type === 'BLOCK') {
    for (const section of templateClone.sections) {
      for (const blockGroup of section.blockGroups) {
        if (blockGroup.id === parentId)
          blockGroup.blocks.splice(index, 0, item as Block)
      }
    }
  }

  return templateClone
}

export const deleteItem = (
  template: TemplateData,
  type: Type,
  id: string
): TemplateData => {
  return {
    ...template,
    sections: template.sections.reduce((sections, section) => {
      if (type === 'SECTION' && section.id === id) return sections

      const newSection: Section = {
        ...section,
        blockGroups: section.blockGroups.reduce((blockGroups, blockGroup) => {
          if (type === 'BLOCK_GROUP' && blockGroup.id === id) return blockGroups

          const newBlockGroup: BlockGroup = {
            ...blockGroup,
            blocks: blockGroup.blocks.reduce((blocks, block) => {
              if (type === 'BLOCK' && block.id === id) return blocks

              const newBlock = { ...block }
              return [...blocks, newBlock]
            }, []),
          }
          return [...blockGroups, newBlockGroup]
        }, []),
      }
      return [...sections, newSection]
    }, []),
  }
}

export const updateItem = (
  template: TemplateData,
  type: Type,
  id: string,
  newValues: object | Block
): TemplateData => {
  return {
    ...template,
    sections: template.sections.map((section) => ({
      ...section,
      ...(type === 'SECTION' && section.id === id ? newValues : {}),
      blockGroups: section.blockGroups.map((blockGroup) => ({
        ...blockGroup,
        ...(type === 'BLOCK_GROUP' && blockGroup.id === id ? newValues : {}),
        blocks: blockGroup.blocks.map((block) =>
          type === 'BLOCK' && block.id === id ? (newValues as Block) : block
        ),
      })),
    })),
  }
}

export const reorderItem = (
  template: TemplateData,
  type: Type,
  id: string,
  parentId: string,
  index: number
) => {
  const item = findItem(type, id, template)
  const itemClone = dynamicClone(type, item)

  const newTemplate = deleteItem(template, type, id)
  return insertItemAt(newTemplate, type, itemClone, parentId, index)
}

export const sanitiseValues = (values: object): void => {
  Object.keys(values).forEach(
    (key) => values[key] === undefined && delete values[key]
  )
}

type ExtractedId = {
  id: string
  type: Type
}

export const extractDragId = (draggableId: string): ExtractedId => {
  if (!draggableId?.includes('|')) return null

  const [type, id] = draggableId.split('|')
  if (['TEMPLATE', 'SECTION', 'BLOCK_GROUP', 'BLOCK'].includes(type))
    return { type: type as Type, id }

  return null
}

export const getBlockInfo = (
  blockKind: BlockKind,
  blockType: BlockTypes
): TypeOption => {
  return getBlockConfig(blockKind, blockType)?.blockTypeConfig
}

export const findBlocks = (
  template: TemplateData,
  filter?: (block: Block) => boolean
): Block[] => {
  const foundBlocks = []

  for (let i = 0; i < template?.sections?.length; i++) {
    const section = template.sections[i]

    for (let j = 0; j < section.blockGroups?.length; j++) {
      const blockGroup = section.blockGroups[j]

      if (filter) {
        const blocks = blockGroup.blocks?.filter(filter)
        foundBlocks.push(...blocks)
      } else {
        foundBlocks.push(...blockGroup.blocks)
      }
    }
  }

  return foundBlocks
}

export const sanitiseTemplate = (
  template: TemplateData,
  templateType: TemplateType
): [TemplateData, Block[]] => {
  const removedBlocks = []

  const sanitisedTemplate: TemplateData = {
    ...template,
    sections: template.sections.map((section) => ({
      ...section,
      blockGroups: section.blockGroups.map((blockGroup) => ({
        ...blockGroup,
        blocks: blockGroup.blocks.filter((block) => {
          const blockInfo = getBlockInfo(block.kind, block.type)
          // Block info can fail for invalid blocks

          if (blockInfo?.forbiddenTemplateTypes?.includes(templateType)) {
            removedBlocks.push(block)
            return false
          }

          if (blockInfo?.exclusiveTemplateTypes) {
            if (!blockInfo?.exclusiveTemplateTypes?.includes(templateType)) {
              removedBlocks.push(block)
              return false
            }
          }

          return true
        }),
      })),
    })),
  }

  return [sanitisedTemplate, removedBlocks]
}
